import React, { useMemo, useState, useEffect } from 'react';
import Chart from 'react-apexcharts';
import { ApexOptions } from 'apexcharts';
import { getHubLocationChart } from '@/services/api/dashboard';

interface HubData {
    name: string;
    value: number;
    color: string;
    percentage: string;
    id: string;
}

interface HubLocationChartProps {
    onSeeAll?: () => void;
}

const CHART_COLORS = [
    '#FBBF24',
    '#8B5CF6',
    '#3B82F6',
    '#14B8A6',
    '#D1D1D1',
];

const HubLocationChart: React.FC<HubLocationChartProps> = ({ onSeeAll }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState<HubData[]>([]);

    useEffect(() => {
        const fetchChartData = async () => {
            try {
                setLoading(true);
                const res = await getHubLocationChart();
                if (res?.data && Array.isArray(res.data)) {
                    // Sort descending by totalVehicle
                    const sorted = [...res.data].sort(
                        (a: any, b: any) => b.totalVehicle - a.totalVehicle
                    );

                    const top4 = sorted.slice(0, 4);
                    const rest = sorted.slice(4);

                    const result: HubData[] = top4.map((item: any, index: number) => ({
                        id: item.hubID.toString(),
                        name: item.hubName || '',
                        value: item.totalVehicle,
                        color: CHART_COLORS[index],
                        percentage: `${Math.round(item.percentageOfTotal)}%`,
                    }));

                    if (rest.length > 0) {
                        const othersValue = rest.reduce(
                            (sum: number, item: any) => sum + item.totalVehicle, 0
                        );
                        const othersPercentage = rest.reduce(
                            (sum: number, item: any) => sum + item.percentageOfTotal, 0
                        );
                        result.push({
                            id: 'others',
                            name: 'Others',
                            value: othersValue,
                            color: CHART_COLORS[4],
                            percentage: `${Math.round(othersPercentage)}%`,
                        });
                    }

                    setChartData(result);
                }
            } catch (error) {
                console.error("Failed to fetch hub location data:", error);
            } finally {
                setLoading(false);
            }
        };

        fetchChartData();
    }, []);

    const chartOptions: ApexOptions = useMemo(() => ({
        chart: {
            type: 'donut',
            height: 280,
            animations: {
                enabled: true,
                easing: 'easeinout',
                speed: 800,
            },
        },
        plotOptions: {
            pie: {
                startAngle: 0,
                endAngle: 360,
                expandOnClick: false,
                offsetX: 0,
                offsetY: 0,
                customScale: 1,
                dataLabels: {
                    offset: 0,
                    minAngleToShowLabel: 10
                },
                donut: {
                    size: '75%',
                    background: 'transparent',
                    labels: {
                        show: false,
                    }
                }
            }
        },
        colors: chartData.map(item => item.color),
        labels: chartData.map(item => item.name),
        dataLabels: {
            enabled: false,
        },
        legend: {
            show: false,
        },
        stroke: {
            show: true,
            width: 4,
            colors: ['#FFFFFF'],
        },
        tooltip: {
            enabled: true,
            y: {
                formatter: function (value) {
                    return value.toLocaleString();
                }
            },
            style: {
                fontSize: '14px',
                fontFamily: 'inherit',
            }
        },
        states: {
            hover: {
                filter: {
                    type: 'lighten',
                    value: 0.1,
                }
            },
            active: {
                filter: {
                    type: 'none',
                }
            }
        },
        responsive: [{
            breakpoint: 480,
            options: {
                chart: {
                    height: 240
                },
                plotOptions: {
                    pie: {
                        donut: {
                            size: '70%'
                        }
                    }
                }
            }
        }]
    }), [chartData]);

    const series = useMemo(() =>
        chartData.map(item => item.value),
        [chartData]
    );

    return (
        <div className="bg-white rounded-2xl border border-gray-200 px-6 pt-5 pb-6 shadow-sm h-full">
            {/* Header */}
            <div className="flex items-center justify-between pb-4 border-b border-gray-200">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 flex items-center justify-center">
                        <i className="ri-file-chart-line text-lg text-neutral-900" />
                    </div>
                    <h3 className="text-base font-medium text-gray-900" style={{ letterSpacing: '-0.011em', lineHeight: '24px' }}>
                        Hub Location
                    </h3>
                </div>

                <button
                    onClick={onSeeAll}
                    className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
                >
                    See All
                </button>
            </div>

            {/* Chart */}
            <div className="flex justify-center mb-2">
                <div style={{ width: '100%', maxWidth: '280px', aspectRatio: '1/1' }} className="flex items-center justify-center">
                    {loading ? (
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                    ) : (
                        <Chart
                            options={chartOptions}
                            series={series}
                            type="donut"
                            height="100%"
                        />
                    )}
                </div>
            </div>

            {/* Custom Legend */}
            <div className={`space-y-3 ${loading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}>
                {chartData.map((item) => (
                    <div key={item.id} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <div
                                className="w-3 h-3 rounded-full"
                                style={{
                                    backgroundColor: item.color,
                                    border: '2px solid white',
                                    boxShadow: '0px 2px 4px 0px #1B1C1D0A'
                                }}
                            />
                            <span
                                className="font-medium"
                                style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.006em',
                                    color: '#5C5C5C'
                                }}
                            >
                                {item.name}
                            </span>
                        </div>
                        <div className="flex items-center gap-3">
                            <span
                                className="font-medium"
                                style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.006em',
                                    color: '#5C5C5C'
                                }}
                            >
                                {item.value.toLocaleString()}
                            </span>
                            <span style={{ fontSize: '14px', color: '#D1D1D1' }}>•</span>
                            <span
                                className="font-normal w-8 text-left"
                                style={{
                                    fontSize: '14px',
                                    lineHeight: '20px',
                                    letterSpacing: '-0.006em',
                                    color: '#A3A3A3'
                                }}
                            >
                                {item.percentage}
                            </span>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default HubLocationChart;
